import { notification } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    OrderType,
    PaymentForm,
    ProductOrderItem,
    TaskRejectProducts,
} from "tm-shared/types";

import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { buildOfflineCheckoutOrder } from "@/pages/WorkPlan/Tasks/utils";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { clearTaskPayload } from "@/store/slices/progress";
import { addItemToCheckout, setTaskCompleted } from "@/store/slices/work";

interface UseTaskCompletionArgs {
    task: TaskRejectProducts;
    marketId: string;
    rejectProductsList: Record<string, ProductOrderItem>;
}

export const useTaskCompletion = ({
    task,
    marketId,
    rejectProductsList,
}: UseTaskCompletionArgs) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const taskId = task.id;

    const { makeTaskCompleteCommands } = useMakeCommand();

    const { markets } = useAppSelector((state) => state.work);
    const { marketsPriceLists, priceLists, offlineMode } = useAppSelector(
        (state) => state.offline,
    );

    const [loading, setLoading] = useState(false);

    const currentPriceList = priceLists?.[marketsPriceLists?.[marketId]] || {};

    const orderId = task?.context?.orderId;

    const getProductsToReturn = () => {
        return Object.values(rejectProductsList)
            .filter((product) => product.count > 0)
            .map((p) => ({
                ...p,
                price: 0,
            }));
    };

    const handleConfirm = async () => {
        if (!marketId || !taskId || !orderId) {
            notification.error({
                message: "Неможливо виконати задачу — відсутні параметри",
            });
            return;
        }

        setLoading(true);

        const productsToReturn = getProductsToReturn();

        if (productsToReturn.length === 0) {
            notification.error({
                message: "Ви не вказали продуктів для повернення",
            });
            setLoading(false);

            return;
        }

        if (offlineMode) {
            const checkout = buildOfflineCheckoutOrder({
                currentPriceList,
                paymentForm: PaymentForm.Card2,
                orderType: OrderType.R,
                market: markets?.[marketId],
                orderId: orderId,
                productsArr: productsToReturn,
            });

            dispatch(
                addItemToCheckout({
                    checkoutList: {
                        [orderId]: checkout,
                    },
                }),
            );
        }

        const orderSaveParams = {
            products: productsToReturn,
            orderId,
            paymentForm: PaymentForm.Card2,
        };

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: orderSaveParams,
            },
            marketId,
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { marketId, taskId, orderId },
                commands: [
                    makeCommand({
                        type: "postOrderSaveOne",
                        title: "Збереження заявки",
                        params: orderSaveParams,
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "getMarketInventoryGetThunk",
                        title: "Синхронізація залишків",
                        params: { marketId },
                    }),
                ],
            }),
        );

        dispatch(clearTaskPayload({ taskId }));
        dispatch(setTaskCompleted({ marketId, taskId }));
        navigate(ROUTES.WorkPlan, { replace: true });

        setLoading(false);
    };

    return { loading, handleConfirm };
};
